/**
 * Carrom AI Player
 *
 * Geometry-based AI with 3 difficulty levels:
 * - Easy: random angle with slight bias toward coin clusters, random power
 * - Medium: find nearest own coin, calculate angle to pocket it, add noise
 * - Hard: evaluate all shots (own coins × 4 pockets), pick best geometry
 */

import {
  CarromPlayer,
  CarromDifficulty,
  CoinState,
  ShotInput,
  BOARD_SIZE,
  BORDER_WIDTH,
  CENTER,
  POCKET_POSITIONS,
  STRIKER_RADIUS,
  COIN_RADIUS,
  Vector2D,
} from './types';
import { CarromEngine } from './CarromEngine';
import { CarromPhysics } from './CarromPhysics';

interface ShotCandidate {
  shot: ShotInput;
  score: number;
}

export class CarromAI {
  private difficulty: CarromDifficulty;
  private thinkingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: CarromDifficulty) {
    this.difficulty = difficulty;
  }

  /**
   * Calculate the best shot for the AI player.
   */
  calculateShot(
    engine: CarromEngine,
    player: CarromPlayer
  ): ShotInput {
    switch (this.difficulty) {
      case 'easy':
        return this.calculateEasyShot(engine, player);
      case 'medium':
        return this.calculateMediumShot(engine, player);
      case 'hard':
        return this.calculateHardShot(engine, player);
      default:
        return this.calculateEasyShot(engine, player);
    }
  }

  /**
   * Schedule an AI action with appropriate delay for the difficulty level.
   */
  scheduleShot(
    engine: CarromEngine,
    player: CarromPlayer,
    callback: (shot: ShotInput) => void
  ): void {
    const delay = this.getThinkingDelay();

    this.thinkingTimeout = setTimeout(() => {
      const shot = this.calculateShot(engine, player);
      callback(shot);
    }, delay);
  }

  destroy(): void {
    if (this.thinkingTimeout) {
      clearTimeout(this.thinkingTimeout);
      this.thinkingTimeout = null;
    }
  }

  // =========================================================================
  // Easy AI
  // =========================================================================

  private calculateEasyShot(
    engine: CarromEngine,
    player: CarromPlayer
  ): ShotInput {
    const baselineY = engine.getBaselineY(player);
    const xRange = engine.getStrikerXRange();

    // Random striker position
    const strikerX = xRange.min + Math.random() * (xRange.max - xRange.min);

    // Bias toward center of board with some randomness
    const targetX = CENTER + (Math.random() - 0.5) * 200;
    const targetY = CENTER + (Math.random() - 0.5) * 200;

    const angle = Math.atan2(targetY - baselineY, targetX - strikerX);
    const power = 0.3 + Math.random() * 0.5;

    return { strikerX, angle, power };
  }

  // =========================================================================
  // Medium AI
  // =========================================================================

  private calculateMediumShot(
    engine: CarromEngine,
    player: CarromPlayer
  ): ShotInput {
    const assignment = engine.getPlayerAssignment(player);
    const activeCoins = engine.getActiveCoins();
    const ownCoins = activeCoins.filter((c) => c.type === assignment);
    const baselineY = engine.getBaselineY(player);
    const xRange = engine.getStrikerXRange();

    if (ownCoins.length === 0) {
      // No own coins left, try queen or random
      const queen = activeCoins.find((c) => c.type === 'queen');
      if (queen) {
        return this.aimAtCoin(queen, baselineY, xRange);
      }
      return this.calculateEasyShot(engine, player);
    }

    // Find nearest own coin to any pocket
    let bestCoin: CoinState | null = null;
    let bestDist = Infinity;

    for (const coin of ownCoins) {
      for (const pocket of POCKET_POSITIONS) {
        const dist = CarromPhysics.distance(coin, pocket);
        if (dist < bestDist) {
          bestDist = dist;
          bestCoin = coin;
        }
      }
    }

    if (!bestCoin) {
      return this.calculateEasyShot(engine, player);
    }

    const shot = this.aimAtCoin(bestCoin, baselineY, xRange);

    // Add noise for medium difficulty
    const noise = (Math.random() - 0.5) * 0.15;
    shot.angle += noise;
    shot.power = Math.max(0.2, Math.min(0.9, shot.power + (Math.random() - 0.5) * 0.2));

    return shot;
  }

  // =========================================================================
  // Hard AI
  // =========================================================================

  private calculateHardShot(
    engine: CarromEngine,
    player: CarromPlayer
  ): ShotInput {
    const assignment = engine.getPlayerAssignment(player);
    const activeCoins = engine.getActiveCoins();
    const ownCoins = activeCoins.filter((c) => c.type === assignment);
    const baselineY = engine.getBaselineY(player);
    const xRange = engine.getStrikerXRange();

    const candidates: ShotCandidate[] = [];

    // Evaluate all own coins × 4 pockets
    const targetCoins = ownCoins.length > 0 ? ownCoins : activeCoins.filter((c) => c.type === 'queen');

    for (const coin of targetCoins) {
      for (const pocket of POCKET_POSITIONS) {
        const candidate = this.evaluateShot(coin, pocket, baselineY, xRange, activeCoins);
        if (candidate) {
          candidates.push(candidate);
        }
      }
    }

    if (candidates.length === 0) {
      return this.calculateMediumShot(engine, player);
    }

    // Sort by score (higher is better)
    candidates.sort((a, b) => b.score - a.score);

    // Pick the best shot with minimal noise
    const best = candidates[0];
    const noise = (Math.random() - 0.5) * 0.03;
    best.shot.angle += noise;

    return best.shot;
  }

  private evaluateShot(
    coin: CoinState,
    pocket: Vector2D,
    baselineY: number,
    xRange: { min: number; max: number },
    allCoins: CoinState[]
  ): ShotCandidate | null {
    // Calculate the direction from coin to pocket
    const coinToPocketAngle = Math.atan2(pocket.y - coin.y, pocket.x - coin.x);

    // The striker needs to hit the coin from the opposite side
    const hitX = coin.x - Math.cos(coinToPocketAngle) * (COIN_RADIUS + STRIKER_RADIUS);
    const hitY = coin.y - Math.sin(coinToPocketAngle) * (COIN_RADIUS + STRIKER_RADIUS);

    // Calculate striker position on baseline
    const strikerX = Math.max(xRange.min, Math.min(xRange.max, hitX));

    // Calculate angle from striker to hit point
    const angle = Math.atan2(hitY - baselineY, hitX - strikerX);

    // Check if the angle is reasonable (not shooting backward)
    const isPlayer1 = baselineY > CENTER;
    if (isPlayer1 && Math.sin(angle) > 0.1) return null; // Player 1 should shoot upward
    if (!isPlayer1 && Math.sin(angle) < -0.1) return null; // Player 2 should shoot downward

    // Calculate distance for power
    const distToHit = CarromPhysics.distance(
      { x: strikerX, y: baselineY },
      { x: hitX, y: hitY }
    );
    const distCoinToPocket = CarromPhysics.distance(coin, pocket);

    // Power based on total distance
    const totalDist = distToHit + distCoinToPocket;
    const power = Math.min(0.9, Math.max(0.25, totalDist / 800));

    // Score the shot
    let score = 100;

    // Prefer shorter distances
    score -= totalDist * 0.1;

    // Prefer good alignment (striker → coin → pocket should be roughly straight)
    const strikerToCoinAngle = Math.atan2(coin.y - baselineY, coin.x - strikerX);
    const angleDiff = Math.abs(this.normalizeAngle(strikerToCoinAngle - coinToPocketAngle));
    score -= angleDiff * 20;

    // Penalize if path is blocked by other coins
    const blocked = this.isPathBlocked(
      { x: strikerX, y: baselineY },
      coin,
      allCoins.filter((c) => c.id !== coin.id)
    );
    if (blocked) score -= 50;

    // Penalize if coin-to-pocket path is blocked
    const pocketBlocked = this.isPathBlocked(
      coin,
      pocket,
      allCoins.filter((c) => c.id !== coin.id)
    );
    if (pocketBlocked) score -= 40;

    return {
      shot: { strikerX, angle, power },
      score: Math.max(0, score),
    };
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  private aimAtCoin(
    coin: CoinState,
    baselineY: number,
    xRange: { min: number; max: number }
  ): ShotInput {
    // Find the nearest pocket to this coin
    let nearestPocket = POCKET_POSITIONS[0];
    let nearestDist = Infinity;

    for (const pocket of POCKET_POSITIONS) {
      const dist = CarromPhysics.distance(coin, pocket);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestPocket = pocket;
      }
    }

    // Calculate hit point (opposite side of coin from pocket)
    const coinToPocketAngle = Math.atan2(
      nearestPocket.y - coin.y,
      nearestPocket.x - coin.x
    );
    const hitX = coin.x - Math.cos(coinToPocketAngle) * (COIN_RADIUS + STRIKER_RADIUS);
    const hitY = coin.y - Math.sin(coinToPocketAngle) * (COIN_RADIUS + STRIKER_RADIUS);

    const strikerX = Math.max(xRange.min, Math.min(xRange.max, hitX));
    const angle = Math.atan2(hitY - baselineY, hitX - strikerX);

    const dist = CarromPhysics.distance(
      { x: strikerX, y: baselineY },
      { x: hitX, y: hitY }
    );
    const power = Math.min(0.85, Math.max(0.3, dist / 600));

    return { strikerX, angle, power };
  }

  private isPathBlocked(
    from: Vector2D,
    to: Vector2D,
    obstacles: CoinState[]
  ): boolean {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return false;

    const nx = dx / len;
    const ny = dy / len;

    for (const obs of obstacles) {
      // Project obstacle onto the line
      const ox = obs.x - from.x;
      const oy = obs.y - from.y;
      const proj = ox * nx + oy * ny;

      if (proj < 0 || proj > len) continue;

      // Perpendicular distance
      const perpDist = Math.abs(ox * ny - oy * nx);
      if (perpDist < COIN_RADIUS + STRIKER_RADIUS) {
        return true;
      }
    }

    return false;
  }

  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle < -Math.PI) angle += 2 * Math.PI;
    return angle;
  }

  private getThinkingDelay(): number {
    switch (this.difficulty) {
      case 'easy':
        return 500 + Math.random() * 500;
      case 'medium':
        return 800 + Math.random() * 700;
      case 'hard':
        return 1000 + Math.random() * 1000;
      default:
        return 500;
    }
  }
}
